import React from "react";
import { formatDistance } from "date-fns";
import { Device } from "types/device.types";

// Import all styles from the separate file
import {
  ListContainer,
  DeviceItem,
  DisabledDeviceItem,
  DeviceMainInfo,
  DeviceName,
  DeviceSecondaryInfo,
  StatusInfo,
  StatusBadge,
  ViewButton,
  StatusMessageBox,
  WifiIcon,
  DeviceItemContent,
} from "styles/devices/devicesMapList.styles";
import { ProgressSpinner } from "primereact/progressspinner";
import { useAuth } from "context/AuthContext";
import { buildDeviceDetailsPath } from "utils/deviceNavigation";

interface DevicesListComponentProps {
  devices: Device[];
  selectedDevice?: Device | null;
  isLoading?: boolean;
  onDeviceSelect?: (device: Device | null) => void;
  viewType?: "cards" | "map" | "table";
}

const DevicesListComponent: React.FC<DevicesListComponentProps> = ({
  devices,
  selectedDevice,
  isLoading,
  onDeviceSelect,
  viewType = "cards", // Default to cards view if not specified
}) => {
  const { user } = useAuth();
  const isAdmin = user?.role === "ADMIN";

  const formatUpdatedDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return formatDistance(date, new Date(), { addSuffix: true });
    } catch (error) {
      return "Unknown";
    }
  };

  // Helper function to check if device is pending or rejected
  const isDeviceRestricted = (status: string) => {
    return status === "pending" || status === "rejected";
  };

  // Helper function to get appropriate status message
  const getStatusMessage = (status: string) => {
    if (status === "pending") {
      return "Waiting for approval";
    } else if (status === "rejected") {
      return "Device rejected";
    }
    return "";
  };

  const handleDeviceClick = (device: Device) => {
    if (!isDeviceRestricted(device.linkStatus) && onDeviceSelect) {
      // If already selected, deselect
      if (selectedDevice?.id === device.id) {
        onDeviceSelect(null);
      } else {
        onDeviceSelect(device);
      }
    }
  };

  if (devices.length === 0) {
    return (
      <ListContainer>
        <DeviceItem>
          <p>No devices found.</p>
        </DeviceItem>
      </ListContainer>
    );
  }

  if (isLoading) {
    return (
      <ListContainer>
        <DeviceItem>
          <ProgressSpinner />
        </DeviceItem>
      </ListContainer>
    );
  }

  return (
    <ListContainer>
      {devices.map((device) => {
        const restricted = isDeviceRestricted(device.linkStatus);
        const statusMessage = restricted
          ? getStatusMessage(device.linkStatus)
          : "";
        const DeviceItemComponent = restricted
          ? DisabledDeviceItem
          : DeviceItem;
        const isSelected = selectedDevice?.id === device.id;

        // Check if device has alarms
        const shadowData = device.shadowData || {};
        const alarmValue = shadowData.alarms ?? 0;

        // Check if device has alarms based on thresholds
        const hasError = alarmValue > 255;
        const hasWarning = alarmValue >= 1 && alarmValue <= 255;
        const $hasAlarm = hasError || hasWarning;

        const alarmSeverity = (() => {
          if (alarmValue > 255) {
            return "Error";
          } else if (alarmValue >= 1 && alarmValue <= 255) {
            return "Warning";
          } else {
            return "No alarms";
          }
        })();

        return (
          <DeviceItemComponent
            key={device.id}
            isSelected={isSelected}
            $hasAlarm={!restricted && $hasAlarm}
            alarmSeverity={alarmSeverity}
            onClick={() => handleDeviceClick(device)}
          >
            <DeviceItemContent>
              <div style={{ display: "flex", alignItems: "center" }}>
                <WifiIcon
                  connected={!!device.iotConnectionStatus?.connected}
                  className="pi pi-wifi"
                />
                <DeviceMainInfo>
                  <DeviceName>{device.serialNumber}</DeviceName>
                  {!restricted ? (
                    <DeviceSecondaryInfo>
                      <span>
                        LG firmware: {device.firmwareVersion || "N/A"}
                      </span>
                      {viewType === "cards" && (
                        <span>
                          Updated: {formatUpdatedDate(device.updatedAt)}
                        </span>
                      )}
                      {viewType === "map" &&
                        device?.DeviceLocation?.address && (
                          <span>
                            Location: {device?.DeviceLocation?.address}
                          </span>
                        )}
                    </DeviceSecondaryInfo>
                  ) : (
                    <DeviceSecondaryInfo>
                      <span>Device information unavailable until approved</span>
                    </DeviceSecondaryInfo>
                  )}
                </DeviceMainInfo>
              </div>
            </DeviceItemContent>

            <StatusInfo>
              <StatusBadge status={device.linkStatus}>
                {device.linkStatus}
              </StatusBadge>
            </StatusInfo>

            {!restricted ? (
              <ViewButton
                to={buildDeviceDetailsPath({
                  serialNumber: device.serialNumber,
                  linkId: device.linkId,
                  isAdminView: isAdmin,
                })}
                onClick={(e) => e.stopPropagation()}
              >
                View Details
              </ViewButton>
            ) : (
              <StatusMessageBox status={device.linkStatus}>
                {statusMessage}
              </StatusMessageBox>
            )}
          </DeviceItemComponent>
        );
      })}
    </ListContainer>
  );
};

export default DevicesListComponent;
